import React, {useEffect, useRef, useState} from "react";
import {
  Box, Stack, Typography, InputBase, Button, IconButton, Popover, Select, MenuItem, Slider, Switch, CircularProgress,
  Collapse,
} from "@mui/material";
import {useAtom, useAtomValue} from "jotai";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  AiSparklesIcon, CheckmarkCircle01Icon, ChevronDownIcon, ChevronRightIcon, Copy01Icon, File02Icon, MessageQuestionIcon, SendIcon,
} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {KnowledgeManagerRepository} from "../../../../repositories/KnowledgeManagerRepository";
import {Panel} from "../../dashboard/components/Panel";
import {documentsAtom, queriesAtom} from "../atoms";

const controlSx = {height: 44, display: "flex", alignItems: "center", gap: 1, px: 1.5, borderRadius: "12px", bgcolor: "#FAFAFD", color: "#6B6B80", fontSize: 13};

const MODE_INFO = [
  {mode: "naive", desc: "Direct vector similarity search on chunks"},
  {mode: "local", desc: "Entity-centric graph neighborhood search"},
  {mode: "global", desc: "Community-level summarization search"},
  {mode: "hybrid", desc: "Combines naive vector + graph search"},
  {mode: "mix", desc: "Integrates local + global graph search"},
];

const modeLabel = (mode) => `${mode.charAt(0).toUpperCase()}${mode.slice(1)} search`;

const formatRelativeTime = (iso) => {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7) return `${diffDay} days ago`;
  return new Date(iso).toLocaleDateString("en-US", {month: "short", day: "numeric"});
};

export const QueryTab = ({topic}) => {
  const documents = useAtomValue(documentsAtom);
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("hybrid");
  const [topK, setTopK] = useState(topic.config?.topK || 60);
  const [onlyNeedContext, setOnlyNeedContext] = useState(false);
  const [modeAnchor, setModeAnchor] = useState(null);
  const [isQuerying, setIsQuerying] = useState(false);
  const [currentResult, setCurrentResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [allQueries, setQueries] = useAtom(queriesAtom);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const resultRef = useRef(null);

  const topicDocuments = documents.filter(d => d.topicId === topic.id);
  const indexedCount = topicDocuments.length > 0
    ? topicDocuments.filter(d => d.status === "indexed").length
    : (topic.documentCount || 0);

  // queriesAtom is shared across topics (fetched once in TopicDetailsPanel when the topic is
  // selected) - guard against the brief window where it still holds the previous topic's data.
  const history = allQueries.filter(q => q.topicId === topic.id);

  const timedQueries = history.filter(qr => !!qr.processingTimeMs);
  const avgResponseTimeMs = timedQueries.length > 0
    ? timedQueries.reduce((sum, qr) => sum + qr.processingTimeMs, 0) / timedQueries.length
    : null;

  const handleQuery = async () => {
    if (!query.trim() || isQuerying) return;
    setIsQuerying(true);
    setCurrentResult(null);
    try {
      const result = await KnowledgeManagerRepository.executeQuery(topic.id, {
        query: query.trim(), mode, topK, onlyNeedContext,
      });
      setCurrentResult(result);
      setQueries(prev => [result, ...prev]);
    } catch (err) {
      toast.error("Query failed");
    } finally {
      setIsQuerying(false);
    }
  };

  const handleCopy = () => {
    if (!currentResult) return;
    navigator.clipboard.writeText(currentResult.response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    if (currentResult && resultRef.current) resultRef.current.scrollIntoView({behavior: "smooth"});
  }, [currentResult]);

  useEffect(() => {
    setQuery("");
    setCurrentResult(null);
    setCopied(false);
  }, [topic.id]);

  return (
    <Stack gap={2}>
      <Panel sx={{p: 2.5}}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5} sx={{mb: 0.5}}>
          <Typography sx={{fontSize: 15, fontWeight: 700, color: "#23264D"}}>Ask this knowledge base</Typography>
          <Box
            onClick={(e) => setModeAnchor(e.currentTarget)}
            sx={{
              cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, px: 1.25, py: 0.5, flexShrink: 0,
              borderRadius: "999px", bgcolor: "rgba(132, 145, 247, 0.12)", color: "#4B55C9",
              "&:hover": {bgcolor: "rgba(132, 145, 247, 0.18)"},
            }}
          >
            <HugeiconsIcon icon={AiSparklesIcon} size={13}/>
            <Typography sx={{fontSize: 12, fontWeight: 700, whiteSpace: "nowrap"}}>{modeLabel(mode)}</Typography>
          </Box>
        </Stack>

        <Typography sx={{fontSize: 12, color: "#8A8AA0", mb: 2}}>
          Answers are grounded in <Box component="span" sx={{color: "#4B55C9", fontWeight: 700}}>{indexedCount}</Box> indexed document{indexedCount === 1 ? "" : "s"}.
        </Typography>

        <Stack direction="row" alignItems="center" gap={1} sx={{...controlSx, pr: 0.75}}>
          <HugeiconsIcon icon={MessageQuestionIcon} size={16} color={"#8491F7"}/>
          <InputBase
            fullWidth value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={`Ask a question about ${topic.name?.toLowerCase() || "this topic"}...`}
            disabled={isQuerying}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleQuery();
              }
            }}
            sx={{flex: 1, fontSize: 13, color: "#23264D"}}
          />
          <Button
            startIcon={isQuerying ? <CircularProgress size={13} sx={{color: "#fff"}}/> : <HugeiconsIcon icon={SendIcon} size={13}/>}
            onClick={handleQuery}
            disabled={!query.trim() || isQuerying}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px', px: 2, whiteSpace: "nowrap",
              bgcolor: '#374151', color: '#fff', '&:hover': {bgcolor: '#1F2937'},
              '&.Mui-disabled': {bgcolor: '#D1D5DB', color: '#9CA3AF'},
            }}
          >
            {currentResult ? "Ask again" : "Ask"}
          </Button>
        </Stack>

        <Popover
          open={Boolean(modeAnchor)} anchorEl={modeAnchor} onClose={() => setModeAnchor(null)}
          anchorOrigin={{vertical: "bottom", horizontal: "right"}} transformOrigin={{vertical: "top", horizontal: "right"}}
          PaperProps={{sx: {borderRadius: "16px", mt: 1, p: 2, width: 300, boxShadow: "0 24px 70px rgba(35, 38, 77, 0.18)"}}}
        >
          <Typography sx={{fontSize: 12, fontWeight: 700, color: "#23264D", mb: 1}}>Search settings</Typography>
          <Box sx={{mb: 1.5}}>
            <Typography sx={{fontSize: 11, color: "#8A8AA0", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.05em"}}>Search mode</Typography>
            <Select fullWidth size="small" value={mode} onChange={(e) => setMode(e.target.value)}
                    sx={{borderRadius: "10px", bgcolor: "#FAFAFD", fontSize: 13, "& .MuiOutlinedInput-notchedOutline": {border: "none"}}}>
              {MODE_INFO.map(m => <MenuItem key={m.mode} value={m.mode}>{modeLabel(m.mode)}</MenuItem>)}
            </Select>
            <Typography sx={{fontSize: 11, color: "#8A8AA0", mt: 0.75}}>
              {MODE_INFO.find(m => m.mode === mode)?.desc}
            </Typography>
          </Box>
          <Box sx={{mb: 1.5}}>
            <Typography sx={{fontSize: 11, color: "#8A8AA0", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em"}}>Top K: {topK}</Typography>
            <Slider value={topK} onChange={(e, v) => setTopK(v)} min={1} max={200} step={1} size="small" sx={{color: "#8491F7"}}/>
          </Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{fontSize: 11, color: "#8A8AA0", textTransform: "uppercase", letterSpacing: "0.05em"}}>Only need context</Typography>
            <Switch checked={onlyNeedContext} onChange={(e) => setOnlyNeedContext(e.target.checked)}
                    sx={{"& .MuiSwitch-switchBase.Mui-checked": {color: "#8491F7"}, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {bgcolor: "#8491F7"}}}/>
          </Stack>
        </Popover>
      </Panel>

      {isQuerying && (
        <Panel sx={{py: 4, alignItems: "center"}}>
          <CircularProgress size={22} sx={{mb: 1.5, color: "#8491F7"}}/>
          <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>
            Searching using <Box component="strong" sx={{color: "#23264D"}}>{modeLabel(mode)}</Box>...
          </Typography>
        </Panel>
      )}

      {currentResult && !isQuerying && (
        <Box ref={resultRef}>
          <Panel sx={{p: 2.5}}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
              <Stack direction="row" alignItems="center" gap={1.25}>
                <Box sx={{
                  width: 34, height: 34, borderRadius: "10px", bgcolor: "rgba(132, 145, 247, 0.12)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <HugeiconsIcon icon={AiSparklesIcon} size={16} color={"#8491F7"}/>
                </Box>
                <Box>
                  <Typography sx={{fontSize: 14, fontWeight: 700, color: "#23264D"}}>Knowledge answer</Typography>
                  <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
                    {modeLabel(currentResult.mode)} · {formatRelativeTime(currentResult.timestamp)}
                    {!!currentResult.processingTimeMs && ` · ${(currentResult.processingTimeMs / 1000).toFixed(1)}s`}
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                onClick={handleCopy}
                sx={{width: 34, height: 34, borderRadius: "10px", border: "1px solid #ECECF3", color: "#6B6B80", flexShrink: 0}}
              >
                {copied ? <HugeiconsIcon icon={CheckmarkCircle01Icon} size={16} color={"#2F9A6A"}/> : <HugeiconsIcon icon={Copy01Icon} size={16}/>}
              </IconButton>
            </Stack>

            <Box sx={{mt: 2}}>
              <Stack direction="row" alignItems="center" gap={1} sx={{mb: 1}}>
                <Box sx={{
                  width: 20, height: 20, borderRadius: "6px", bgcolor: "#374151", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0,
                }}>
                  Q
                </Box>
                <Typography sx={{fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#8A8AA0", textTransform: "uppercase"}}>
                  Your question
                </Typography>
              </Stack>
              <Box sx={{bgcolor: "#FAFAFD", borderRadius: "12px", p: 1.5}}>
                <Typography sx={{fontSize: 13, color: "#23264D"}}>{currentResult.query}</Typography>
              </Box>
            </Box>

            <Box sx={{mt: 1.5}}>
              <Stack direction="row" alignItems="center" gap={1} sx={{mb: 1}}>
                <Box sx={{
                  width: 20, height: 20, borderRadius: "6px", bgcolor: "#8491F7", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0,
                }}>
                  A
                </Box>
                <Typography sx={{fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#4B55C9", textTransform: "uppercase"}}>
                  Answer
                </Typography>
              </Stack>
              <Box sx={{bgcolor: "rgba(132, 145, 247, 0.08)", borderRadius: "12px", p: 1.5}}>
                <Typography sx={{fontSize: 13, color: "#23264D", whiteSpace: "pre-wrap", lineHeight: 1.6}}>
                  {currentResult.response}
                </Typography>
              </Box>
            </Box>

            {currentResult.sources?.length > 0 && (
              <Box sx={{mt: 2, pt: 1.5, borderTop: "1px solid #ECECF3"}}>
                <Typography sx={{fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#8A8AA0", textTransform: "uppercase", mb: 1}}>
                  Sources used
                </Typography>
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  {currentResult.sources.map((src, i) => (
                    <Stack key={src} direction="row" alignItems="center" gap={0.75} sx={{bgcolor: "#FAFAFD", borderRadius: "10px", px: 1.25, py: 0.75}}>
                      <HugeiconsIcon icon={File02Icon} size={12} color={"#8A8AA0"}/>
                      <Box sx={{
                        width: 16, height: 16, borderRadius: "50%", bgcolor: "rgba(132, 145, 247, 0.16)", color: "#4B55C9",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, flexShrink: 0,
                      }}>
                        {i + 1}
                      </Box>
                      <Typography sx={{fontSize: 12, color: "#23264D"}} noWrap>{src}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </Panel>
        </Box>
      )}

      <Stack
        direction="row" alignItems="flex-end" justifyContent="space-between" sx={{mt: 1, cursor: "pointer"}}
        onClick={() => setHistoryExpanded(v => !v)}
      >
        <Box>
          <Typography sx={{fontSize: 15, fontWeight: 700, color: "#23264D"}}>Query history</Typography>
          <Typography sx={{fontSize: 12, color: "#8A8AA0", mt: 0.25}}>
            {history.length} question{history.length === 1 ? "" : "s"} in this topic
            {avgResponseTimeMs != null && ` · Avg response ${(avgResponseTimeMs / 1000).toFixed(1)}s`}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" gap={0.5}>
          {history.length > 0 && (
            <Button
              onClick={(e) => {e.stopPropagation(); setQueries(prev => prev.filter(q => q.topicId !== topic.id));}}
              sx={{textTransform: 'none', fontSize: 12, fontWeight: 600, color: '#6B6B80', '&:hover': {bgcolor: 'transparent', color: '#374151'}}}
            >
              Clear history
            </Button>
          )}
          <Box sx={{
            width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', bgcolor: historyExpanded ? '#8491F7' : '#F0F0F6', color: historyExpanded ? '#fff' : '#6B6B80',
            transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.3s ease, color 0.3s ease',
            transform: historyExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
            <HugeiconsIcon icon={ChevronDownIcon} size={15}/>
          </Box>
        </Stack>
      </Stack>

      <Collapse in={historyExpanded}>
        {history.length === 0 ? (
          <Panel sx={{py: 4, alignItems: "center"}}>
            <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>No questions asked yet</Typography>
          </Panel>
        ) : (
          <Stack gap={1} sx={{mt: 1.5}}>
            {history.slice(0, 20).map(qr => {
              const active = currentResult?.id === qr.id;
              return (
                <Stack
                  key={qr.id} direction="row" alignItems="flex-start" gap={1.25}
                  onClick={() => {setQuery(qr.query); setCurrentResult(qr);}}
                  sx={{
                    p: 1.5, borderRadius: '12px', cursor: "pointer", border: '1px solid transparent',
                    backgroundColor: active ? 'rgba(132, 145, 247, 0.08)' : '#FAFAFD',
                    borderColor: active ? 'rgba(132, 145, 247, 0.35)' : 'transparent',
                    "&:hover": {backgroundColor: active ? 'rgba(132, 145, 247, 0.12)' : '#F2F2F8'},
                  }}
                >
                  <Box sx={{
                    width: 22, height: 22, borderRadius: "6px", bgcolor: "#374151", color: "#fff", mt: 0.1,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0,
                  }}>
                    Q
                  </Box>
                  <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}} noWrap>{qr.query}</Typography>
                    <Stack direction="row" alignItems="flex-start" gap={0.75} sx={{mt: 0.25}}>
                      <Box sx={{
                        width: 16, height: 16, borderRadius: "5px", bgcolor: "#2F9A6A", color: "#fff", mt: 0.15,
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, flexShrink: 0,
                      }}>
                        A
                      </Box>
                      <Typography sx={{fontSize: 12, color: "#8A8AA0", minWidth: 0}} noWrap>{qr.response}</Typography>
                    </Stack>
                  </Box>
                  <Stack alignItems="flex-end" gap={0.5} sx={{flexShrink: 0, ml: 1}}>
                    <Typography sx={{fontSize: 11, color: "#8A8AA0", whiteSpace: "nowrap"}}>{formatRelativeTime(qr.timestamp)}</Typography>
                    <HugeiconsIcon icon={ChevronRightIcon} size={14} color={"#C9CAD8"}/>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Collapse>
    </Stack>
  );
};
